import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const ContactPage: React.FC = () => {
  const { me } = useAuth();
  const location = useLocation();
  const [category, setCategory] = React.useState<'suggestion' | 'complaint' | 'issue' | 'abuse' | 'other'>('suggestion');
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState<string>('');
  const [files, setFiles] = React.useState<File[]>([]);
  const [privacyOk, setPrivacyOk] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [successId, setSuccessId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (me?.email && !contactEmail) setContactEmail(me.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.email]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const cat = (params.get('category') || '').trim().toLowerCase();
    if (cat && ['suggestion', 'issue', 'complaint', 'abuse', 'other'].includes(cat)) {
      setCategory(cat as any);
    }
    const subj = (params.get('subject') || '').trim();
    if (subj && !subject.trim()) setSubject(subj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Contact</title>
      </Helmet>

      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">Contact</h1>
        <p className="text-sm text-muted-foreground">
          Sugestii, reclamații sau probleme întâmpinate. Ne ajută să îmbunătățim serviciile.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 text-sm">
        <div className="font-semibold">Date firmă</div>
        <div className="mt-2 space-y-1 text-muted-foreground">
          <div>
            <span className="text-foreground">Global Linknet SRL</span>
          </div>
          <div>
            Sediu: județul Argeș, Pitești, Str. Dorobanților, nr. 14, bl. 51, et. 1, ap. 8
          </div>
          <div>Registrul Comerțului: J03/1287/2023</div>
          <div>CUI: 48291648</div>
          <div>
            E-mail: <span className="text-foreground">office@globallinknet.ro</span>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        {successId ? (
          <div className="space-y-2">
            <div className="text-sm font-semibold">Mulțumim!</div>
            <div className="text-sm text-muted-foreground">
              Cererea ta a fost înregistrată cu ID <span className="text-foreground">#{successId}</span>.
            </div>
            <button
              type="button"
              className="mt-2 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90"
              onClick={() => {
                setSuccessId(null);
                setCategory('suggestion');
                setSubject('');
                setMessage('');
                setFiles([]);
              }}
            >
              Trimite alta
            </button>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!privacyOk) {
                window.alert('Te rugăm să confirmi că ai citit Politica de confidențialitate.');
                return;
              }
              if (!me?.email && !contactEmail.trim()) {
                window.alert('Te rugăm să introduci un e-mail de contact.');
                return;
              }
              setIsSubmitting(true);
              try {
                const fd = new FormData();
                fd.append('category', category);
                fd.append('subject', subject);
                fd.append('message', message);
                if (contactEmail.trim()) fd.append('contact_email', contactEmail.trim());
                files.slice(0, 5).forEach((f) => fd.append('attachments', f));

                const resp = await api.post('/api/contact/submit', fd, {
                  headers: { 'Content-Type': 'multipart/form-data' },
                });
                if (resp.data?.ok) {
                  setSuccessId(Number(resp.data.id));
                } else {
                  window.alert(resp.data?.detail || 'Nu am putut trimite mesajul.');
                }
              } catch (err: any) {
                const detail = err?.response?.data?.detail;
                window.alert(detail || err?.message || 'Eroare la trimitere.');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="space-y-1">
                <div className="text-sm font-medium">Tip</div>
                <select
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as any)}
                >
                  <option value="suggestion">Sugestie</option>
                  <option value="issue">Problemă întâmpinată</option>
                  <option value="complaint">Reclamație</option>
                  <option value="abuse">Raportează abuz</option>
                  <option value="other">Altceva</option>
                </select>
              </label>

              <label className="space-y-1">
                <div className="text-sm font-medium">E-mail de contact (opțional)</div>
                <input
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  placeholder="ex: nume@email.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </label>
            </div>

            <label className="space-y-1 block">
              <div className="text-sm font-medium">Subiect</div>
              <input
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="ex: Nu pot încărca un fișier la temă"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </label>

            <label className="space-y-1 block">
              <div className="text-sm font-medium">Mesaj</div>
              <textarea
                className="w-full min-h-36 rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="Descrie problema / sugestia cât mai clar (pași, ce te așteptai, ce s-a întâmplat)."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              />
            </label>

            <label className="space-y-1 block">
              <div className="text-sm font-medium">Atașamente (opțional)</div>
              <div className="text-xs text-muted-foreground">
                Poți încărca până la 5 fișiere (imagini sau documente), max. 10MB/fișier. Recomandat: capturi de ecran.
              </div>
              <input
                type="file"
                multiple
                className="mt-2 block w-full text-sm"
                onChange={(e) => {
                  const next = Array.from(e.target.files || []);
                  setFiles(next);
                }}
              />
              {files.length > 0 && (
                <div className="mt-2 text-xs text-muted-foreground">
                  Selectate: <span className="text-foreground">{files.map((f) => f.name).join(', ')}</span>
                </div>
              )}
            </label>

            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={privacyOk}
                onChange={(e) => setPrivacyOk(e.target.checked)}
              />
              <span className="text-muted-foreground">
                Am citit Politica de confidențialitate și sunt de acord ca datele transmise să fie prelucrate pentru
                soluționarea solicitării.
              </span>
            </label>

            <div className="flex items-center justify-end gap-2">
              <button
                type="submit"
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Se trimite...' : 'Trimite'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactPage;
